'use client';

import React from 'react';
import styled from 'styled-components';

export interface MapLegendItem {
  /** CSS color string used for the swatch fill. */
  color: string;
  /** Visible label for the item. */
  label: string;
  /** Optional stable key — falls back to label. */
  key?: string;
}

export interface MapLegendProps extends React.HTMLAttributes<HTMLDivElement> {
  items: MapLegendItem[];
  /** Optional title rendered above the items. */
  title?: string;
}

const Root = styled.div`
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spaceAlias.sm} ${(p) => p.theme.spaceAlias.md};
  box-shadow: ${(p) => p.theme.elevation.raised.shadow};
  font-family: ${(p) => p.theme.fontFamily.ui};
  color: ${(p) => p.theme.colors.neutral.ink};
  display: inline-flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spaceAlias.xs};
  max-width: 260px;
`;

const Title = styled.div`
  font-size: ${(p) => p.theme.fontSize.xs};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Items = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spaceAlias.xs};
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  font-size: ${(p) => p.theme.fontSize.xs};
`;

const Swatch = styled.span`
  width: 14px;
  height: 14px;
  border-radius: ${(p) => p.theme.radius.xs};
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

const Label = styled.span`
  font-weight: ${(p) => p.theme.fontWeight.medium};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Compact colour-key legend for map overlays. Each item renders a
 * coloured swatch + label. Designed to sit absolutely-positioned
 * inside a map canvas; the consumer provides positioning.
 */
export function MapLegend({ items, title, ...rest }: MapLegendProps) {
  return (
    <Root {...rest}>
      {title && <Title data-legend-title>{title}</Title>}
      <Items role="list">
        {items.map((item) => (
          <Item key={item.key ?? item.label} role="listitem">
            <Swatch data-legend-swatch style={{ backgroundColor: item.color }} aria-hidden />
            <Label>{item.label}</Label>
          </Item>
        ))}
      </Items>
    </Root>
  );
}
